// WebRTC configuration and utilities

// Define a basic CallInfo interface for this file
type CallState = 'idle' | 'initiating' | 'ringing' | 'connecting' | 'connected' | 'ended';

interface CallInfo {
  state: CallState;
  type: 'audio' | 'video' | null;
  isIncoming: boolean;
  callerId: string | null;
  callerName: string | null;
  callerPhoto?: string;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  isMuted: boolean;
  isVideoOn: boolean;
  isSpeakerOn: boolean;
  callDuration: number;
  error: string | null;
}

/**
 * Fetch ICE servers from Metered API
 */
export const getIceServers = async (): Promise<RTCIceServer[]> => {
  try {
    const res = await fetch(
      "https://mangeshtest.metered.live/api/v1/turn/credentials?apiKey=68f5cf854ce6bbebcb7484c40eb477f8a7a6"
    );
    const data = await res.json();

    //console.log("Metered raw response:", data);

    // The API returns an array directly
    if (Array.isArray(data)) {
      //console.log("Successfully fetched ICE servers from Metered API:", data);
      return data;
    }

    // Fallback: check if wrapped in an object
    if (data.iceServers && Array.isArray(data.iceServers)) {
      //console.log("Successfully fetched ICE servers from Metered API:", data.iceServers);
      return data.iceServers;
    }

    // Fallback: check for old format with uris
    if (data.uris && Array.isArray(data.uris) && data.username && data.credential) {
      const iceServers = data.uris.map((uri: string) => ({
        urls: uri,
        username: data.username,
        credential: data.credential,
      }));
      //console.log("Successfully mapped ICE servers from Metered API:", iceServers);
      return iceServers;
    }

    throw new Error("Invalid response format from Metered API");
  } catch (error) {
    //console.error("Error fetching ICE servers from Metered API:", error);
    throw error;
  }
};

/**
 * Create RTC configuration with dynamic ICE servers
 */
export const createRTCConfiguration = async (): Promise<RTCConfiguration> => {
  const iceServers = await getIceServers();
  
  return {
    iceServers,
    iceCandidatePoolSize: 10
  };
};

// Legacy static configuration (deprecated - use createRTCConfiguration instead)
// This is kept for backward compatibility but should not be used
export const RTC_CONFIGURATION: RTCConfiguration = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' }
  ],
  iceCandidatePoolSize: 10
};

export const MEDIA_CONSTRAINTS = {
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    sampleRate: 44100
  },
  video: {
    width: { ideal: 1280, max: 1920 },
    height: { ideal: 720, max: 1080 },
    frameRate: { ideal: 30, max: 60 },
    facingMode: 'user'
  }
};

export const SCREEN_SHARE_CONSTRAINTS = {
  video: {
    mediaSource: 'screen' as string,
    width: { ideal: 1920 },
    height: { ideal: 1080 },
    frameRate: { ideal: 30 }
  },
  audio: false
};

/**
 * Get user media with error handling and legacy fallback
 */
export const getUserMedia = async (constraints: MediaStreamConstraints): Promise<MediaStream> => {
  try {
    // Check if modern API is available
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      return stream;
    } else {
      // Legacy API fallback
      const getUserMediaLegacy = (navigator as Navigator & {
        getUserMedia?: (constraints: MediaStreamConstraints, success: (stream: MediaStream) => void, error: (error: Error) => void) => void;
        webkitGetUserMedia?: (constraints: MediaStreamConstraints, success: (stream: MediaStream) => void, error: (error: Error) => void) => void;
        mozGetUserMedia?: (constraints: MediaStreamConstraints, success: (stream: MediaStream) => void, error: (error: Error) => void) => void;
      }).getUserMedia || 
                               (navigator as Navigator & { webkitGetUserMedia?: (constraints: MediaStreamConstraints, success: (stream: MediaStream) => void, error: (error: Error) => void) => void }).webkitGetUserMedia || 
                               (navigator as Navigator & { mozGetUserMedia?: (constraints: MediaStreamConstraints, success: (stream: MediaStream) => void, error: (error: Error) => void) => void }).mozGetUserMedia;
      
      if (!getUserMediaLegacy) {
        const errorMessage = `
getUserMedia is not supported in this browser or context.

SOLUTIONS:
1. Use HTTPS: Install ngrok (npm install -g ngrok) then run 'ngrok http 3002'
2. Use Chrome with flags: chrome --unsafely-treat-insecure-origin-as-secure=http://${window.location.host} --disable-web-security
3. Use Firefox: Firefox allows getUserMedia on localhost HTTP
4. Use the testing script: ./start-chrome-testing.sh

Current URL: ${window.location.href}
Browser: ${navigator.userAgent}
        `;
        throw new Error(errorMessage);
      }
      
      // Use legacy getUserMedia with Promise wrapper
      const stream = await new Promise<MediaStream>((resolve, reject) => {
        getUserMediaLegacy.call(navigator, constraints, resolve, reject);
      });
      return stream;
    }
  } catch (error) {
    console.error('Error accessing media devices:', error);
    
    // Provide user-friendly error messages
    if (error instanceof DOMException) {
      switch (error.name) {
        case 'NotAllowedError':
          throw new Error('Camera and microphone access denied. Please allow access to continue.');
        case 'NotFoundError':
          throw new Error('No camera or microphone found. Please check your devices.');
        case 'NotReadableError':
          throw new Error('Camera or microphone is being used by another application.');
        case 'OverconstrainedError':
          throw new Error('Camera or microphone constraints cannot be satisfied.');
        default:
          throw new Error('Failed to access camera or microphone.');
      }
    }
    
    throw error;
  }
};

/**
 * Get screen share stream
 */
export const getScreenShare = async (): Promise<MediaStream> => {
  try {
    const stream = await navigator.mediaDevices.getDisplayMedia(SCREEN_SHARE_CONSTRAINTS);
    return stream;
  } catch (error) {
    console.error('Error accessing screen share:', error);
    
    if (error instanceof DOMException) {
      switch (error.name) {
        case 'NotAllowedError':
          throw new Error('Screen sharing denied. Please allow screen sharing to continue.');
        case 'NotFoundError':
          throw new Error('Screen sharing not supported on this device.');
        default:
          throw new Error('Failed to access screen sharing.');
      }
    }
    
    throw error;
  }
};

/**
 * Create a new RTCPeerConnection with proper configuration
 */
export const createPeerConnection = async (): Promise<RTCPeerConnection> => {
  const configuration = await createRTCConfiguration();
  return new RTCPeerConnection(configuration);
};

/**
 * Create a fallback RTC configuration with basic STUN servers
 * Used when Metered API is unavailable
 */
export const createFallbackRTCConfiguration = (): RTCConfiguration => {
  return {
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' }
    ],
    iceCandidatePoolSize: 10
  };
};

/**
 * Create a new RTCPeerConnection with static configuration (legacy)
 * @deprecated Use createPeerConnection() or createPeerConnectionWithHandlers() instead
 */
export const createPeerConnectionLegacy = (): RTCPeerConnection => {
  console.warn('createPeerConnectionLegacy is deprecated. Use createPeerConnection() or createPeerConnectionWithHandlers() instead.');
  return new RTCPeerConnection(RTC_CONFIGURATION);
};

/**
 * Create a peer connection with dynamic ICE servers and event handlers
 */
export const createPeerConnectionWithHandlers = async (options: {
  onIceCandidate?: (event: RTCPeerConnectionIceEvent) => void;
  onTrack?: (event: RTCTrackEvent) => void;
  onConnectionStateChange?: (state: string) => void;
  onIceConnectionStateChange?: (state: string) => void;
  onIceGatheringStateChange?: (state: string) => void;
  onCallDurationStart?: () => void;
  onCallEnd?: () => void;
  currentUserId: string;
  isEndingCallRef?: React.MutableRefObject<boolean>;
  callInfoState?: string;
  callDurationIntervalRef?: React.MutableRefObject<NodeJS.Timeout | null>;
  setCallInfo?: (updater: (prev: CallInfo) => CallInfo) => void;
}): Promise<RTCPeerConnection> => {
  const configuration = await createRTCConfiguration();
  const peerConnection = new RTCPeerConnection(configuration);
  
  // Set up event handlers
  peerConnection.onicecandidate = (event) => {
    if (event.candidate && options.onIceCandidate) {
      options.onIceCandidate(event);
    }
  };

  peerConnection.ontrack = (event) => {
    console.log(`[${options.currentUserId}] Received remote stream:`, event.streams);
    if (event.streams && event.streams[0]) {
      // Handle remote audio
      let audio = document.getElementById("remoteAudio") as HTMLAudioElement;
      if (!audio) {
        audio = document.createElement("audio");
        audio.id = "remoteAudio";
        audio.autoplay = true;
        document.body.appendChild(audio);
      }
      audio.srcObject = event.streams[0];
      
      // Call custom onTrack handler if provided
      if (options.onTrack) {
        options.onTrack(event);
      }
    }
  };

  peerConnection.onconnectionstatechange = () => {
    const state = peerConnection.connectionState;
    console.log(`[${options.currentUserId}] Connection state changed to:`, state);
    
    if (options.onConnectionStateChange) {
      options.onConnectionStateChange(state);
    }
    
    // Handle connected state
    if (state === 'connected') {
      if (options.setCallInfo) {
        options.setCallInfo(prev => ({ ...prev, state: 'connected' }));
      }
      
      // Start call duration timer
      if (options.callDurationIntervalRef) {
        if (options.callDurationIntervalRef.current) {
          clearInterval(options.callDurationIntervalRef.current);
        }
        options.callDurationIntervalRef.current = setInterval(() => {
          if (options.setCallInfo) {
            options.setCallInfo(prev => ({ ...prev, callDuration: prev.callDuration + 1 }));
          }
        }, 1000);
      }
      
      if (options.onCallDurationStart) {
        options.onCallDurationStart();
      }
    } else if (state === 'disconnected' || state === 'failed') {
      console.log(`[${options.currentUserId}] Connection lost`);
      if (options.onCallEnd) {
        options.onCallEnd();
      }
    }
  };

  peerConnection.oniceconnectionstatechange = () => {
    const state = peerConnection.iceConnectionState;
    console.log(`[${options.currentUserId}] ICE connection state:`, state);
    
    if (options.onIceConnectionStateChange) {
      options.onIceConnectionStateChange(state);
    }
    
    if (state === 'failed') {
      console.log(`[${options.currentUserId}] ICE connection failed, attempting to restart ICE`);
      peerConnection.restartIce();
    }
  };

  peerConnection.onicegatheringstatechange = () => {
    const state = peerConnection.iceGatheringState;
    console.log(`[${options.currentUserId}] ICE gathering state:`, state);
    
    if (options.onIceGatheringStateChange) {
      options.onIceGatheringStateChange(state);
    }
  };

  return peerConnection;
};

/**
 * Add media tracks to peer connection
 */
export const addMediaTracks = (
  peerConnection: RTCPeerConnection,
  stream: MediaStream
): void => {
  stream.getTracks().forEach(track => {
    peerConnection.addTrack(track, stream);
  });
};

/**
 * Add local audio stream to peer connection
 */
export const addLocalAudioStream = async (
  peerConnection: RTCPeerConnection
): Promise<MediaStream> => {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  stream.getTracks().forEach(track => peerConnection.addTrack(track, stream));
  return stream;
};

/**
 * Remove media tracks from peer connection
 */
export const removeMediaTracks = (peerConnection: RTCPeerConnection): void => {
  const senders = peerConnection.getSenders();
  senders.forEach(sender => {
    if (sender.track) {
      peerConnection.removeTrack(sender);
    }
  });
};

/**
 * Stop all tracks in a stream
 */
export const stopStream = (stream: MediaStream | null): void => {
  if (stream) {
    stream.getTracks().forEach(track => {
      track.stop();
    });
  }
};

/**
 * Get audio level from stream
 */
export const getAudioLevel = (stream: MediaStream): Promise<number> => {
  return new Promise((resolve) => {
    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    const microphone = audioContext.createMediaStreamSource(stream);
    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    analyser.fftSize = 256;
    microphone.connect(analyser);

    const getLevel = () => {
      analyser.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
      resolve(average / 255); // Normalize to 0-1
    };

    requestAnimationFrame(getLevel);
  });
};

/**
 * Check if device supports video
 */
export const supportsVideo = async (): Promise<boolean> => {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.some(device => device.kind === 'videoinput');
  } catch {
    return false;
  }
};

/**
 * Check if device supports audio
 */
export const supportsAudio = async (): Promise<boolean> => {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.some(device => device.kind === 'audioinput');
  } catch {
    return false;
  }
};

/**
 * Get available cameras
 */
export const getCameras = async (): Promise<MediaDeviceInfo[]> => {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter(device => device.kind === 'videoinput');
  } catch {
    return [];
  }
};

/**
 * Get available microphones
 */
export const getMicrophones = async (): Promise<MediaDeviceInfo[]> => {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter(device => device.kind === 'audioinput');
  } catch {
    return [];
  }
};

/**
 * Switch camera
 */
export const switchCamera = async (
  currentStream: MediaStream,
  facingMode: 'user' | 'environment' = 'environment'
): Promise<MediaStream> => {
  const videoTrack = currentStream.getVideoTracks()[0];
  if (!videoTrack) {
    throw new Error('No video track found');
  }

  const newConstraints = {
    video: {
      ...MEDIA_CONSTRAINTS.video,
      facingMode
    },
    audio: true
  };

  const newStream = await getUserMedia(newConstraints);
  
  // Replace video track in current stream
  const newVideoTrack = newStream.getVideoTracks()[0];
  currentStream.removeTrack(videoTrack);
  currentStream.addTrack(newVideoTrack);
  
  // Stop the old video track
  videoTrack.stop();
  
  return currentStream;
};

/**
 * Start call duration timer
 */
export const startCallDuration = (
  callDurationIntervalRef: React.MutableRefObject<NodeJS.Timeout | null>,
  setCallInfo: (updater: (prev: CallInfo) => CallInfo) => void
): void => {
  if (callDurationIntervalRef.current) {
    clearInterval(callDurationIntervalRef.current);
  }
  
  callDurationIntervalRef.current = setInterval(() => {
    setCallInfo(prev => ({ ...prev, callDuration: prev.callDuration + 1 }));
  }, 1000);
};

/**
 * Stop call duration timer
 */
export const stopCallDuration = (
  callDurationIntervalRef: React.MutableRefObject<NodeJS.Timeout | null>
): void => {
  if (callDurationIntervalRef.current) {
    clearInterval(callDurationIntervalRef.current);
    callDurationIntervalRef.current = null;
  }
};

/**
 * Format call duration
 */
export const formatCallDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  
  return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Check if browser supports WebRTC
 */
export const supportsWebRTC = (): boolean => {
  return !!(
    typeof window !== 'undefined' &&
    window.RTCPeerConnection &&
    window.RTCSessionDescription &&
    window.RTCIceCandidate &&
    navigator.mediaDevices &&
    navigator.mediaDevices.getUserMedia
  );
};

/**
 * Get connection quality based on ICE connection state
 */
export const getConnectionQuality = (connectionState: string): 'excellent' | 'good' | 'poor' | 'disconnected' => {
  switch (connectionState) {
    case 'connected':
      return 'excellent';
    case 'connecting':
      return 'good';
    case 'disconnected':
    case 'failed':
    case 'closed':
      return 'disconnected';
    default:
      return 'poor';
  }
};
